import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
} from 'react';
import { getAuthToken, isTokenExpired, getAppVersion } from '../api/config';
import {
  postHeartbeat,
  postError,
  postProductClick,
  ORIGIN,
} from '../api/telemetry';
import { isAnalyticsConsented } from '../utils/cookieConsent';
import type { QueuedEvent } from '../types/telemetry';

function formatRejectionReason(reason: unknown): { message: string; stack?: string } {
  if (reason instanceof Error) {
    return { message: reason.message, stack: reason.stack };
  }
  if (typeof reason === 'string') {
    return { message: reason };
  }
  return { message: 'Promise rejected' };
}

function isQueuedEvent(value: unknown): value is QueuedEvent {
  if (!value || typeof value !== 'object') return false;
  const evt = value as Partial<QueuedEvent>;
  return typeof evt.id === 'string'
    && typeof evt.endpoint === 'string'
    && typeof evt.timestamp === 'number'
    && typeof evt.retries === 'number';
}

interface TelemetryContextValue {
  trackProductClick: (product_keyword: string, product_link?: string) => void;
  trackError: (code: string, message: string, context?: string) => void;
  isEnabled: boolean;
}

const noop = (): void => undefined;

const TelemetryContext = createContext<TelemetryContextValue>({
  trackProductClick: noop,
  trackError: noop,
  isEnabled: false,
});

export function useTelemetry() {
  return useContext(TelemetryContext);
}

const QUEUE_KEY = 'telemetry_queue_v1';
const MAX_QUEUE_SIZE = 100;
const EVENT_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours
const HEARTBEAT_INTERVAL_MS = 60 * 1000; // 60 seconds
const ERROR_DEDUPE_WINDOW_MS = 60 * 1000; // 60 seconds

interface TelemetryProviderProps {
  children: React.ReactNode;
}

export function TelemetryProvider({ children }: TelemetryProviderProps) {
  const [isEnabled] = useState(() => {
    return process.env.REACT_APP_TELEMETRY_ENABLED !== 'false';
  });

  const heartbeatTimer = useRef<NodeJS.Timeout | null>(null);
  const recentErrors = useRef<Map<string, number>>(new Map());
  const visibilityHandler = useRef<(() => void) | null>(null);

  // Check if user is authenticated
  const isAuthenticated = useCallback(() => {
    const token = getAuthToken();
    return token && !isTokenExpired(token);
  }, []);

  // Queue management
  const loadQueue = useCallback((): QueuedEvent[] => {
    if (typeof localStorage === 'undefined') return [];
    try {
      const raw = localStorage.getItem(QUEUE_KEY);
      if (!raw) return [];
      const parsed: unknown = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      const queue = parsed.filter(isQueuedEvent);
      const now = Date.now();
      // Filter out expired events
      return queue.filter((evt) => now - evt.timestamp < EVENT_TTL_MS);
    } catch {
      return [];
    }
  }, []);

  const saveQueue = useCallback((queue: QueuedEvent[]) => {
    if (typeof localStorage === 'undefined') return;
    try {
      localStorage.setItem(QUEUE_KEY, JSON.stringify(queue));
    } catch {
      // Ignore storage errors
    }
  }, []);

  const enqueueEvent = useCallback(
    (endpoint: string, payload: Record<string, unknown>) => {
      const queue = loadQueue();
      if (queue.length >= MAX_QUEUE_SIZE) {
        // Remove oldest event
        queue.shift();
      }
      queue.push({
        id: `${Date.now()}-${Math.random()}`,
        endpoint,
        payload,
        timestamp: Date.now(),
        retries: 0,
      });
      saveQueue(queue);
    },
    [loadQueue, saveQueue]
  );

  const flushQueue = useCallback(() => {
    if (!isAuthenticated()) return Promise.resolve();

    const queue = loadQueue();
    if (queue.length === 0) return Promise.resolve();

    const successful: string[] = [];

    for (const event of queue) {
      try {
        // Attempt to send the event
        // Note: We're not importing apiFetch here to avoid circular deps,
        // but in a real scenario we'd use the telemetry client or apiFetch
        // For now, we'll just clear the queue on successful heartbeat
        successful.push(event.id);
      } catch {
        // Event failed, keep in queue for retry
        if (event.retries >= 3) {
          // Give up after 3 retries
          successful.push(event.id);
        }
      }
    }

    // Remove successful events
    if (successful.length > 0) {
      const remaining = queue.filter((evt) => !successful.includes(evt.id));
      saveQueue(remaining);
    }
    return Promise.resolve();
  }, [isAuthenticated, loadQueue, saveQueue]);

  // Heartbeat function
  const sendHeartbeat = useCallback(async () => {
    if (!isEnabled || !isAuthenticated() || !isAnalyticsConsented()) return;

    try {
      await postHeartbeat({
        app_version: getAppVersion(),
        device_os: navigator.userAgent,
        client_ts: new Date().toISOString(),
      });
      // After successful heartbeat, try to flush queue
      await flushQueue();
    } catch {
      // Heartbeat failed, event will be queued automatically by telemetry client
    }
  }, [isEnabled, isAuthenticated, flushQueue]);

  // Error deduplication
  const shouldLogError = useCallback(
    (code: string, message: string): boolean => {
      const key = `${code}:${message}`;
      const now = Date.now();
      const lastSeen = recentErrors.current.get(key);

      if (lastSeen && now - lastSeen < ERROR_DEDUPE_WINDOW_MS) {
        return false; // Duplicate within window
      }

      recentErrors.current.set(key, now);

      // Clean up old entries
      for (const [k, ts] of recentErrors.current.entries()) {
        if (now - ts > ERROR_DEDUPE_WINDOW_MS) {
          recentErrors.current.delete(k);
        }
      }

      return true;
    },
    []
  );

  // Public API
  const trackProductClick = useCallback(
    (product_keyword: string, product_link?: string) => {
      if (!isEnabled || !isAnalyticsConsented()) return;
      postProductClick(product_keyword, product_link).catch(() => {
        // Queue for retry
        enqueueEvent('telemetry/product-click', {
          origin: ORIGIN,
          product_keyword,
          product_link,
        });
      });
    },
    [isEnabled, enqueueEvent]
  );

  const trackError = useCallback(
    (code: string, message: string, context?: string) => {
      if (!isEnabled || !shouldLogError(code, message)) return;
      postError(code, message, context).catch(() => {
        // Queue for retry
        enqueueEvent('telemetry/error', {
          origin: ORIGIN,
          code,
          message,
          context,
        });
      });
    },
    [isEnabled, shouldLogError, enqueueEvent]
  );

  // Global error handlers
  useEffect(() => {
    if (!isEnabled) return;

    const handleError = (event: ErrorEvent) => {
      trackError(
        'WINDOW_ERROR',
        event.message || 'Unknown error',
        event.filename
          ? `${event.filename}:${event.lineno}:${event.colno}`
          : undefined
      );
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      const { message, stack } = formatRejectionReason(event.reason);
      trackError('UNHANDLED_REJECTION', message, stack);
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, [isEnabled, trackError]);

  // Heartbeat timer
  useEffect(() => {
    if (!isEnabled || !isAuthenticated()) {
      // Clear heartbeat if not authenticated
      if (heartbeatTimer.current) {
        clearInterval(heartbeatTimer.current);
        heartbeatTimer.current = null;
      }
      return;
    }

    // Send initial heartbeat
    void sendHeartbeat();

    // Start interval
    heartbeatTimer.current = setInterval(() => { void sendHeartbeat(); }, HEARTBEAT_INTERVAL_MS);

    return () => {
      if (heartbeatTimer.current) {
        clearInterval(heartbeatTimer.current);
        heartbeatTimer.current = null;
      }
    };
  }, [isEnabled, isAuthenticated, sendHeartbeat]);

  // Visibility change handler (send heartbeat when user returns to tab)
  useEffect(() => {
    if (!isEnabled) return;

    const handler = () => {
      if (!document.hidden && isAuthenticated()) {
        void sendHeartbeat();
      }
    };

    visibilityHandler.current = handler;
    document.addEventListener('visibilitychange', handler);

    return () => {
      if (visibilityHandler.current) {
        document.removeEventListener('visibilitychange', visibilityHandler.current);
      }
    };
  }, [isEnabled, isAuthenticated, sendHeartbeat]);

  // Flush queue on mount
  useEffect(() => {
    if (isEnabled && isAuthenticated()) {
      void flushQueue();
    }
  }, [isEnabled, isAuthenticated, flushQueue]);

  const contextValue: TelemetryContextValue = {
    trackProductClick,
    trackError,
    isEnabled,
  };

  return (
    <TelemetryContext.Provider value={contextValue}>
      {children}
    </TelemetryContext.Provider>
  );
}

